import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    clientsCompanyResource,
    getPrefixes,
    plansResource,
} from "../../../helpers/configRoutes/backend_helper";
import { useSelector } from "react-redux";
import { validatePhones } from "../../../helpers/forms/Validation";
import CustomInput from "../../../components/form/CustomInput";
import { showFormErrors } from "../../../helpers/showFormErrors";
import BaseModalAnimated from "../../../components/modal/BaseModalAnimated";
import CustomSelect from "../../../components/form/CustomSelect";
import CustomButton from "../../../components/form/CustomButton";
import CustomButtonOval from "../../../components/form/CustomButtonOval";
import { useNavigate } from "react-router-dom";

const ProfileAccountForm = ({ modal, setModal }) => {
    const user = useSelector((state) => state.Login.user);
    const [company, setCompany] = useState({});
    const [loading, setLoading] = useState(false);
    const [prefixes, setPrefixes] = useState([]);
    const [plans, setPlans] = useState([]);
    const navigate = useNavigate();

    const validation = useFormik({
        initialValues: company,
        validationSchema: Yup.object({
            email: Yup.string().email("Invalid email").required("Email is required"),
            name: Yup.string().required("Name is required"),
            prefix_id: Yup.string().required("Prefix is required"),
            phone: validatePhones(true),
            contact_name: Yup.string().required("Contact name is required"),
            address: Yup.string().required("Address is required"),
            invoicing_address: Yup.string().required("Invoicing address is required"),
            dot: Yup.string().required("DOT is required"),
            mc_number: Yup.string().required("MC number is required"),
        }),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);
            const response = await clientsCompanyResource(
                values.id ? "put" : "post",
                values,
                values.id
            );
            if (response.status) {
                setModal({ ...modal, show: false });
            } else {
                setErrors(response.errors);
            }
            setLoading(false);
        },
        enableReinitialize: true,
    });

    useEffect(() => {
        const getDataCompany = async () => {
            const response = await clientsCompanyResource(
                "show",
                {},
                user.company_id
            );
            if (response.status) {
                setCompany(response.company || {});
                Object.keys(response.company).forEach((field) => {
                    validation.setFieldValue(
                        field,
                        response.company[field],
                        false
                    );
                });
            }
        };

        const getCatalogs = async () => {
            const response = await plansResource("get", {
                params: { select: true },
            });
            setPlans(response.plans || []);
        };

        const getPrefixesCatalog = async () => {
            const response = await getPrefixes();
            if (response.status) {
                setPrefixes(response.prefixes || []);
            }
        };

        getDataCompany();
        getCatalogs();
        getPrefixesCatalog();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isProfileSection = !!modal.profile; // true = Company/Profile, false = Billing/Payment

    return (
        <BaseModalAnimated
            modal={modal}
            setModal={setModal}
            title={
                modal.title ||
                (isProfileSection
                    ? "Company & contact details"
                    : "Billing & payment settings")
            }
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                    return false;
                }}
            >
                <div className="bg-cs-bg rounded-2xl overflow-hidden">
                    {/* Header contextual dentro del modal */}
                    <div className="px-5 py-4 border-b border-cs-border bg-cs-surface flex items-center justify-between gap-3">
                        <div className="flex flex-col gap-0.5">
                            <p className="text-xs uppercase tracking-[0.16em] text-cs-primary-dark">
                                {isProfileSection
                                    ? "Account & profile"
                                    : "Billing & payment"}
                            </p>
                            <p className="text-xs text-cs-muted">
                                {isProfileSection
                                    ? "Update your company information and main contact details."
                                    : "Review your current billing info and navigate to manage payment methods."}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setModal({ ...modal, show: false })}
                            className="
                                px-3 py-1.5 rounded-xl
                                bg-cs-bg
                                text-xs text-cs-muted
                                hover:bg-cs-primary-soft hover:text-cs-primary
                                border border-cs-border
                                transition-all
                            "
                        >
                            Close
                        </button>
                    </div>

                    {/* Form body */}
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4 bg-cs-bg-alt">
                        {/* Company Name */}
                        <CustomInput
                            label="Company name"
                            type="text"
                            name="name"
                            labelClassName="text-xs font-medium text-cs-muted mb-1"
                            error={validation?.errors?.name}
                            value={validation?.values?.name || ""}
                            handleChange={validation.handleChange}
                            className="text-xs"
                        />

                        {/* Plan + link */}
                        <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-2 items-end">
                            <CustomSelect
                                label="Subscription plan"
                                options={plans}
                                name="plan_id"
                                disabled={true}
                                labelClassName="text-xs font-medium text-cs-muted mb-1"
                                value={validation?.values?.plan_id}
                                className="text-xs"
                            />
                            {!isProfileSection && (
                                <p
                                    onClick={() => navigate("/subscriptions")}
                                    className="
                                        text-xs text-cs-primary
                                        cursor-pointer
                                        hover:text-cs-primary-hover
                                        hover:underline
                                        text-right
                                    "
                                >
                                    Manage plan
                                </p>
                            )}
                        </div>

                        {/* Contact name */}
                        <CustomInput
                            label="Main contact name"
                            type="text"
                            name="contact_name"
                            labelClassName="text-xs font-medium text-cs-muted mb-1"
                            error={validation?.errors?.contact_name}
                            value={validation?.values?.contact_name || ""}
                            handleChange={validation.handleChange}
                            className="text-xs"
                        />

                        {/* Phone */}
                        <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.6fr)] gap-2">
                            <CustomSelect
                                label="Phone prefix"
                                options={prefixes}
                                name="prefix_id"
                                labelClassName="text-xs font-medium text-cs-muted mb-1"
                                className="text-xs"
                                error={validation?.errors?.prefix_id}
                                value={validation?.values?.prefix_id}
                                handleChange={validation.handleChange}
                            />
                            <CustomInput
                                label="Phone number"
                                type="text"
                                name="phone"
                                labelClassName="text-xs font-medium text-cs-muted mb-1"
                                error={validation?.errors?.phone}
                                value={validation?.values?.phone || ""}
                                handleChange={validation.handleChange}
                                className="text-xs"
                            />
                        </div>

                        {/* Email */}
                        <CustomInput
                            label="Billing / account email"
                            type="text"
                            name="email"
                            labelClassName="text-xs font-medium text-cs-muted mb-1"
                            error={validation?.errors?.email}
                            value={validation?.values?.email || ""}
                            handleChange={validation.handleChange}
                            className="text-xs"
                        />

                        {/* Licenses */}
                        <CustomInput
                            readOnly={true}
                            label="Users / licenses"
                            type="text"
                            name="users"
                            labelClassName="text-xs font-medium text-cs-muted mb-1"
                            value={validation?.values?.users_count || ""}
                            handleChange={validation.handleChange}
                            className="text-xs bg-cs-bg-alt"
                        />

                        {/* Address */}
                        <CustomInput
                            label="Main address"
                            type="text"
                            name="address"
                            labelClassName="text-xs font-medium text-cs-muted mb-1"
                            error={validation?.errors?.address}
                            value={validation?.values?.address || ""}
                            handleChange={validation.handleChange}
                            className="text-xs"
                        />

                        {/* Invoicing address */}
                        <CustomInput
                            label="Invoicing address"
                            type="text"
                            name="invoicing_address"
                            labelClassName="text-xs font-medium text-cs-muted mb-1"
                            error={validation?.errors?.invoicing_address}
                            value={validation?.values?.invoicing_address || ""}
                            handleChange={validation.handleChange}
                            className="text-xs"
                        />

                        {/* PROFILE vs BILLING extra fields */}
                        {isProfileSection ? (
                            <>
                                <CustomInput
                                    label="DOT"
                                    type="number"
                                    name="dot"
                                    labelClassName="text-xs font-medium text-cs-muted mb-1"
                                    error={validation?.errors?.dot}
                                    value={validation?.values?.dot || ""}
                                    handleChange={validation.handleChange}
                                    className="text-xs"
                                />
                                <CustomInput
                                    label="MC number"
                                    type="number"
                                    name="mc_number"
                                    labelClassName="text-xs font-medium text-cs-muted mb-1"
                                    error={validation?.errors?.mc_number}
                                    value={validation?.values?.mc_number || ""}
                                    handleChange={validation.handleChange}
                                    className="text-xs"
                                />
                            </>
                        ) : (
                            <div className="col-span-2 flex flex-col gap-2">
                                <CustomInput
                                    label="Preferred payment method"
                                    type="text"
                                    name="payment_id"
                                    labelClassName="text-xs font-medium text-cs-muted mb-1"
                                    error={validation?.errors?.payment_id}
                                    value={"VISA ending in **25"}
                                    handleChange={validation.handleChange}
                                    className="text-xs"
                                    readOnly
                                />
                                <div className="grid grid-cols-2 gap-2 mt-1">
                                    <CustomButtonOval
                                        loading={loading}
                                        textLoad={"Saving"}
                                        type={"success"}
                                        className="
                                            w-full
                                            bg-cs-primary
                                            hover:bg-cs-primary-hover
                                            text-white
                                            text-xs
                                            rounded-xl
                                        "
                                        onClick={() => navigate("/accounting")}
                                    >
                                        Payment history
                                    </CustomButtonOval>
                                    <CustomButtonOval
                                        loading={loading}
                                        textLoad={"Saving"}
                                        type={"success"}
                                        className="
                                            w-full
                                            bg-cs-bg-alt
                                            text-cs-primary
                                            border border-cs-border
                                            hover:bg-cs-primary-soft
                                            hover:text-cs-primary-dark
                                            text-xs
                                            rounded-xl
                                        "
                                        onClick={() => navigate("/payment-methods")}
                                    >
                                        Manage methods
                                    </CustomButtonOval>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-2 px-5 py-4 bg-cs-bg border-t border-cs-border rounded-b-2xl">
                        <button
                            type="button"
                            onClick={() => setModal({ ...modal, show: false })}
                            className="
                                px-3 py-2 rounded-xl
                                text-xs text-cs-muted
                                bg-cs-bg-alt
                                hover:bg-cs-primary-soft
                                hover:text-cs-primary
                                border border-cs-border
                                transition-all
                            "
                        >
                            Cancel
                        </button>
                        <CustomButton
                            loading={loading}
                            textLoad={"Saving"}
                            type={"success"}
                            className="
                                w-auto px-5 py-2
                                bg-cs-primary
                                hover:bg-cs-primary-hover
                                text-white
                                rounded-xl
                                text-xs font-medium
                                shadow-sm hover:shadow-md
                                flex items-center justify-center gap-1
                            "
                        >
                            {validation.values?.id ? "Save changes" : "Add company"}
                        </CustomButton>
                    </div>
                </div>
            </form>
        </BaseModalAnimated>
    );
};

export default ProfileAccountForm;
